/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Configure Match Booster on a model", description = "Enable Match Booster on a model or parent model, map identifiers, configure enrichment settings, and run the initial enrichment.", lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Configure Match Booster on a model",
        description: "Enable Match Booster on a model or parent model, map identifiers, configure enrichment settings, and run the initial enrichment.",
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Where Match Booster lives in Hightouch",
          "depth": 1,
          "id": "where-match-booster-lives-in-hightouch"
        }, {
          "value": "Enable Match Booster on the model",
          "depth": 0,
          "id": "enable-match-booster-on-the-model"
        }, {
          "value": "Select expansion options",
          "depth": 0,
          "id": "select-expansion-options"
        }, {
          "value": "Map match keys",
          "depth": 0,
          "id": "map-match-keys"
        }, {
          "value": "Match keys reference",
          "depth": 1,
          "id": "match-keys-reference"
        }, {
          "value": "Identifier normalization",
          "depth": 0,
          "id": "identifier-normalization"
        }, {
          "value": "Email",
          "depth": 1,
          "id": "email"
        }, {
          "value": "Phone",
          "depth": 1,
          "id": "phone"
        }, {
          "value": "Postal codes and addresses",
          "depth": 1,
          "id": "postal-codes-and-addresses"
        }, {
          "value": "Hashed names and addresses",
          "depth": 1,
          "id": "hashed-names-and-addresses"
        }, {
          "value": "Set enrichment schedule",
          "depth": 0,
          "id": "set-enrichment-schedule"
        }, {
          "value": "Add enrichment filters (parent models only)",
          "depth": 0,
          "id": "add-enrichment-filters-parent-models-only"
        }, {
          "value": "Run initial enrichment",
          "depth": 0,
          "id": "run-initial-enrichment"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      h3: "h3",
      ol: "ol",
      li: "li",
      ul: "ul",
      img: "img",
      h4: "h4",
      code: "code"
    }, _provideComponents(), props.components), {BtOnly, FeatureFlag} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Match Booster",
        extraText: " as an add-on product."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams, platform admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["A ", _jsx(_components.a, {
                href: "/models/creating-models",
                children: "model"
              }), " or Customer Studio ", _jsx(_components.a, {
                href: "/customer-studio/schema",
                children: "parent model"
              }), " with at least one ", _jsx(_components.a, {
                href: "#match-keys-reference",
                children: "supported identifier"
              }), ", and Match Booster enabled on your workspace"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Configure Match Booster on a model to enrich your first-party identifiers with additional emails, phone numbers, device IDs, and other identifiers from Hightouch's third-party identity graph."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsxs(_components.p, {
        children: ["This page covers the model side of Match Booster setup — enabling enrichment, mapping your identifier columns, configuring settings, and running the initial enrichment. After enrichment completes, ", _jsx(_components.a, {
          href: "/match-booster/enable-in-syncs",
          children: "enable Match Booster in your syncs"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "where-match-booster-lives-in-hightouch",
        children: "Where Match Booster lives in Hightouch"
      }), "\n", _jsx(_components.p, {
        children: "You can enable Match Booster in two places, depending on how you build your audiences:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Approach"
            }), _jsx(_components.th, {
              children: "Best for"
            }), _jsx(_components.th, {
              children: "Where to configure"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Enable on a model"
            }), _jsx(_components.td, {
              children: "Syncs built directly from a model in Activation"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "https://app.hightouch.com/models?from=docs",
                children: _jsx(_components.strong, {
                  children: "Activation > Models"
                })
              }), ", select a model, then open the ", _jsx(_components.strong, {
                children: "Match Booster"
              }), " tab"]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Enable on a parent model"
            }), _jsx(_components.td, {
              children: "Customer Studio audiences built from the schema"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "https://app.hightouch.com/schema?from=docs",
                children: _jsx(_components.strong, {
                  children: "Customer Studio > Schema"
                })
              }), ", select a parent model, then open the ", _jsx(_components.strong, {
                children: "Match Booster"
              }), " tab"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "enable-match-booster-on-the-model",
        children: "Enable Match Booster on the model"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Open the model or parent model where you want to enable Match Booster:"
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["For models: go to ", _jsx(_components.a, {
                href: "https://app.hightouch.com/models?from=docs",
                children: _jsx(_components.strong, {
                  children: "Activation > Models"
                })
              }), ". Select the model and open the ", _jsx(_components.strong, {
                children: "Match Booster"
              }), " tab."]
            }), "\n", _jsxs(_components.li, {
              children: ["For parent models: go to ", _jsx(_components.a, {
                href: "https://app.hightouch.com/schema?from=docs",
                children: _jsx(_components.strong, {
                  children: "Customer Studio > Schema"
                })
              }), ". Select the parent model and open the ", _jsx(_components.strong, {
                children: "Match Booster"
              }), " tab."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Toggle ", _jsx(_components.strong, {
              children: "Enable Match Booster"
            }), " on."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "match-booster/model-match-booster-tab.png",
          alt: "The Match Booster tab on a model, showing the Enable Match Booster toggle, expansion options, and enrichment status."
        })
      }), "\n", _jsx(_components.p, {
        children: "This unlocks the expansion options, identifier mapping, and enrichment schedule sections."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "select-expansion-options",
        children: "Select expansion options"
      }), "\n", _jsx(_components.p, {
        children: "Configure optional expansion behavior before mapping match keys."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Enable Household Expansion"
            }), " — Enable this if you want to target, suppress, or measure at the household level instead of just the individual. Household Expansion matches a single record to a residential address in the identity graph and expands targeting to all people and devices in that household. To use Household targeting, you must also select the Household targeting level during ", _jsx(_components.a, {
              href: "/match-booster/enable-in-syncs",
              children: "sync configuration"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Enable CTV Matching"
            }), " — Enable this if you run ads on connected TV platforms (Roku, LG, Samsung, Vizio). CTV Matching translates your first-party identifiers into CTV IDs and IP addresses that CTV ad platforms use for matching."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "match-booster/mb-expansion-options.png",
          alt: "Expansion options showing the Household Expansion and CTV Matching toggles."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "/match-booster/targeting-levels",
          children: "Targeting levels"
        }), " for guidance on when to use person-level versus household-level matching."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "map-match-keys",
        children: "Map match keys"
      }), "\n", _jsxs(_components.p, {
        children: ["Under ", _jsx(_components.strong, {
          children: "Which identifiers should be used to link your model to Hightouch's identity graph?"
        }), ", map your match keys by selecting a warehouse ", _jsx(_components.strong, {
          children: "Column name"
        }), " and an ", _jsx(_components.strong, {
          children: "Identifier type"
        }), " for each identifier you want to use."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Select the identifiers you want to match into the identity graph. Common mappings include:"
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: "Email (raw or hashed — Hightouch hashes raw values automatically)"
            }), "\n", _jsx(_components.li, {
              children: "Phone number (raw or hashed)"
            }), "\n", _jsx(_components.li, {
              children: "Name and address fields (raw, or SHA256 hashed in US enrichment, for name/address matching)"
            }), "\n", _jsx(_components.li, {
              children: "Device IDs, IP addresses, or mobile ad IDs (for anonymous or CTV use cases)"
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["For name and address matching, you must provide ", _jsx(_components.strong, {
              children: "first name"
            }), ", ", _jsx(_components.strong, {
              children: "last name"
            }), ", and at minimum the ", _jsx(_components.strong, {
              children: "first address line"
            }), " and ", _jsx(_components.strong, {
              children: "postal code"
            }), ". Including city and state improves accuracy."]
          }), "\n", _jsxs(_components.p, {
            children: ["Each name and address field has a plain text identifier type and a SHA256 identifier type, for example ", _jsx(_components.strong, {
              children: "First Name"
            }), " and ", _jsx(_components.strong, {
              children: "First Name SHA256"
            }), ". You can mix types across fields, but map only one type per field. See ", _jsx(_components.a, {
              href: "#hashed-names-and-addresses",
              children: "Hashed names and addresses"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Save"
            }), " when your mappings are complete."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "match-booster/model-mb-matching.png",
          alt: "Match key mapping UI showing warehouse columns mapped to identifier types."
        })
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Select the magic wand icon to automatically map match keys based on your\ncolumn names."
        })
      }), "\n", _jsx(_components.h3, {
        id: "match-keys-reference",
        children: "Match keys reference"
      }), "\n", _jsx(_components.p, {
        children: "Match keys determine which records can be matched, what additional identifiers Match Booster can add, and what enriched identifiers are available in your destination syncs."
      }), "\n", _jsx(_components.h4, {
        id: "known-users",
        children: "Known users"
      }), "\n", _jsx(_components.p, {
        children: "Use known-user matching when you have user-level identifiers like email, phone, MAIDs, or postal address information."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Supported inputs:"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Match key"
            }), _jsx(_components.th, {
              children: "Supported format"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Email"
            }), _jsx(_components.td, {
              children: "Plain text, MD5, SHA256"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Phone"
            }), _jsx(_components.td, {
              children: "Plain text, MD5, SHA256"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "MAIDs"
            }), _jsx(_components.td, {
              children: "Plain text"
            }), _jsx(_components.td, {
              children: "Mobile advertising IDs"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Full postal address"
            }), _jsx(_components.td, {
              children: "Plain text, SHA256"
            }), _jsx(_components.td, {
              children: "First name, last name, street 1, street 2, city, state, postal code"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Enriched identifiers added:"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Identifier"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Email"
            }), _jsx(_components.td, {
              children: "Hashed"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Phone"
            }), _jsx(_components.td, {
              children: "Hashed"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "MAIDs"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "IP address (CTV)"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "First name"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Last name"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "City"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "State"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Postal code"
            }), _jsx(_components.td, {})]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "anonymous-users",
        children: "Anonymous users"
      }), "\n", _jsx(_components.p, {
        children: "Use anonymous matching when you have IP address data but no known user identifiers."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Supported inputs:"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Match key"
            }), _jsx(_components.th, {
              children: "Supported format"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "IP address"
            }), _jsx(_components.td, {
              children: "Plain text"
            }), _jsx(_components.td, {
              children: "Used for anonymous matching"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Enriched identifiers added:"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Identifier"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Email"
            }), _jsx(_components.td, {
              children: "Hashed"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Mobile ad ID"
            }), _jsx(_components.td, {})]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "conversion-events",
        children: "Conversion events"
      }), "\n", _jsx(_components.p, {
        children: "Use conversion matching when you sync conversion records and want ad platforms to match more of those events."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Supported inputs:"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Match key"
            }), _jsx(_components.th, {
              children: "Supported format"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Email"
            }), _jsx(_components.td, {
              children: "Plain text, MD5, SHA256"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Phone"
            }), _jsx(_components.td, {
              children: "Plain text"
            }), _jsx(_components.td, {})]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Enriched identifiers added:"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Identifier"
            }), _jsx(_components.th, {
              children: "Notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Email"
            }), _jsx(_components.td, {
              children: "Hashed"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Phone"
            }), _jsx(_components.td, {
              children: "Hashed"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "First name"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Last name"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "City"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "State"
            }), _jsx(_components.td, {})]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Postal code"
            }), _jsx(_components.td, {})]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "identifier-normalization",
        children: "Identifier normalization"
      }), "\n", _jsx(_components.p, {
        children: "If you pre-hash email, phone, or name and address values, normalize your plaintext identifiers using the rules below so your hashes match what Hightouch requires. EU enrichment requires stricter normalization than US enrichment because the EU identity graph stores values in a different format."
      }), "\n", _jsx(_components.h3, {
        id: "email",
        children: "Email"
      }), "\n", _jsx(_components.p, {
        children: "In all regions, lowercase the email before hashing."
      }), "\n", _jsx(_components.p, {
        children: "For US enrichment, also trim leading and trailing whitespace."
      }), "\n", _jsx(_components.p, {
        children: "For EU enrichment, also:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Remove all whitespace from the email."
        }), "\n", _jsxs(_components.li, {
          children: ["Remove accents (for example, ", _jsx(_components.code, {
            children: "é → e"
          }), ", ", _jsx(_components.code, {
            children: "ü → u"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Replace these special characters: ", _jsx(_components.code, {
            children: "ß → ss"
          }), ", ", _jsx(_components.code, {
            children: "œ → oe"
          }), ", ", _jsx(_components.code, {
            children: "æ → ae"
          }), ", ", _jsx(_components.code, {
            children: "þ → th"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["For Gmail addresses (", _jsx(_components.code, {
            children: "@gmail.com"
          }), "), remove dots and ", _jsx(_components.code, {
            children: "+tag"
          }), " suffixes from the part before the ", _jsx(_components.code, {
            children: "@"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Examples (plaintext → value to hash):"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "Marie.Dupré@example.com"
          }), " → ", _jsx(_components.code, {
            children: "marie.dupre@example.com"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "weiß@example.de"
          }), " → ", _jsx(_components.code, {
            children: "weiss@example.de"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "John.Doe+promo@gmail.com"
          }), " → ", _jsx(_components.code, {
            children: "johndoe@gmail.com"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "phone",
        children: "Phone"
      }), "\n", _jsxs(_components.p, {
        children: ["For EU enrichment, format phone numbers as E.164 (with the leading ", _jsx(_components.code, {
          children: "+"
        }), ", for example ", _jsx(_components.code, {
          children: "+447700900123"
        }), "). This formatting is required for both plaintext and pre-hashed phone values."]
      }), "\n", _jsx(_components.h3, {
        id: "postal-codes-and-addresses",
        children: "Postal codes and addresses"
      }), "\n", _jsx(_components.p, {
        children: "In all regions, lowercase names and address fields (street, city, state)."
      }), "\n", _jsx(_components.p, {
        children: "For postal codes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "US enrichment"
          }), " — Keep only the digits (5-digit ZIP, 3-digit ZIP prefix, or ZIP+4)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "EU enrichment"
          }), " — Remove whitespace but keep letters and numbers. For example, UK ", _jsx(_components.code, {
            children: "SW1A 1AA"
          }), " becomes ", _jsx(_components.code, {
            children: "sw1a1aa"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "hashed-names-and-addresses",
        children: "Hashed names and addresses"
      }), "\n", _jsx(FeatureFlag, {
        featureName: "Hashed name and address matching"
      }), "\n", _jsx(_components.p, {
        children: "If your data policies don't allow plain text names and addresses, you can map SHA256 hashes of any of the seven name and address fields instead: first name, last name, address line 1, address line 2, city, state, and postal code. Hashed name and address matching is available for US enrichment only."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch hashes its identity graph the same way you hash your data, so your hashes only match if you follow these steps exactly:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Standardize addresses to USPS format with a CASS-certified tool."
        }), "\n", _jsx(_components.li, {
          children: "Lowercase each value, trim leading and trailing whitespace, and replace repeated spaces with a single space."
        }), "\n", _jsx(_components.li, {
          children: "Hash each value with SHA-256 and store the hex digest. Uppercase and lowercase hex both work."
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "CASS output is uppercase. If you hash it without lowercasing it first, none of\nyour hashes match, and Hightouch can't detect the mistake."
        })
      }), "\n", _jsx(_components.p, {
        children: "Field-specific rules:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Address line 1"
          }), ": hash the standardized delivery line, for example ", _jsx(_components.code, {
            children: "123 n main st"
          }), ", not ", _jsx(_components.code, {
            children: "123 north main street"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "State"
          }), ": hash the two-letter state code, for example ", _jsx(_components.code, {
            children: "ca"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Postal code"
          }), ": hash the 5-digit ZIP code only. Don't include ZIP+4."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Leave empty values as null. Hightouch treats a hash of an empty string, or any value that isn't a 64-character hex digest, as unmapped."
      }), "\n", _jsx(_components.p, {
        children: "Hashed values only match exactly. Plain text values can also match on partial values, such as the 3-digit ZIP prefix, so expect fewer matches from hashed fields than from plain text ones."
      }), "\n", _jsx(_components.p, {
        children: "Example record:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Field"
            }), _jsx(_components.th, {
              children: "Value to hash"
            }), _jsx(_components.th, {
              children: "SHA256"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "First name"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "jane"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "81f8f6dde88365f3928796ec7aa53f72820b06db8664f5fe76a7eb13e24546a2"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Last name"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "doe"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "799ef92a11af918e3fb741df42934f3b568ed2d93ac1df74f1b8d41a27932a6f"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Address line 1"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "123 n main st"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "34af32dcb5106488f5d8aeeefef7d5c319026dfe54a96e057ddc562825e913d5"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Address line 2"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "apt 4b"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "c9c05614dffb8f5efd84ee5172d61cbbd3b5218565852f421f387aa88eff6525"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "City"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "los angeles"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "9f2608067816e38c85edfb0c3985feff32def8b5dc17bb522ffc2e877e9b386b"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "State"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "ca"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "6959097001d10501ac7d54c0bdb8db61420f658f2922cc26e46d536119a31126"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Postal code"
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "90210"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.code, {
                children: "b83c588da0c6931625f42e0948054a3ade722bfd02c27816305742ed7390ac6c"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "set-enrichment-schedule",
        children: "Set enrichment schedule"
      }), "\n", _jsx(_components.p, {
        children: "Configure how frequently Hightouch enriches your model against the identity graph."
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Enrichment schedule"
        }), " section lets you set a recurring interval (for example, every 1 day). Schedules are capped at 28 days to meet data retention requirements."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "match-booster/model-mb-schedule.png",
          alt: "Enrichment schedule configuration showing an interval of every 1 day."
        })
      }), "\n", _jsx(_components.p, {
        children: "Enrichment creates a secure mapping between your hashed primary keys and the third-party identity graph without storing raw PII."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "add-enrichment-filters-parent-models-only",
        children: "Add enrichment filters (parent models only)"
      }), "\n", _jsx(_components.p, {
        children: "On parent models, you can use enrichment filters to define a subset of records to enrich. Common filters include:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Limiting enrichment to specific regions"
        }), "\n", _jsx(_components.li, {
          children: "Enforcing consent rules by enriching only consented users"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "match-booster/mb-enrichment-filter.png",
          alt: "Enrichment filter configured to enrich only users where Consent equals true."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "run-initial-enrichment",
        children: "Run initial enrichment"
      }), "\n", _jsxs(_components.p, {
        children: ["Select ", _jsx(_components.strong, {
          children: "Initialize Match Booster"
        }), " to run the first enrichment."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "match-booster/mb-initialize.png",
          alt: "The Initialize Match Booster button with the email notification checkbox."
        })
      }), "\n", _jsx(_components.p, {
        children: "The initial run performs a full enrichment and may take longer for large datasets. Subsequent runs only enrich new or updated records."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Initialization is a one-time step that can take up to 72 hours. Select ", _jsx(_components.strong, {
            children: "Email\nme when enrichment is complete"
          }), " to avoid monitoring progress manually."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsx(_components.p, {
        children: "Configuring Match Booster on a model is step one. Enrichment does not flow to ad platforms until you enable Match Booster on a sync."
      }), "\n", _jsx(_components.p, {
        children: "After the initial enrichment completes:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/match-booster/enable-in-syncs",
            children: _jsx(_components.strong, {
              children: "Enable Match Booster in syncs"
            })
          }), " — Turn on Match Booster in your destination syncs to start sending enriched identifiers to ad platforms."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
